import { type MouseEventHandler, useMemo } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@rtkwlf/pro-solid-svg-icons/faLock';
import {
  type ConciergeTierInfo,
  type BlueprintWithOrganizationData,
  type ConciergeTier,
} from '@rtkwlf/track-public';
import { type TFunction } from 'i18next';
import { Link } from 'react-router-dom';

import {
  DataGridPro,
  GridToolbarQuickFilter,
  type GridColDef,
  type GridComparatorFn,
  type GridRenderCellParams,
  type GridEventListener,
} from '@packages/fenrir/components/datagrid';

import { ConciergeTierBadge } from '../../../../components/concierge-tier-badge/concierge-tier-badge';
import { FavoriteButton } from '../../../../components/favorite-button';
import { useFormatNullableDate } from '../../../../components/formatted-date';
import { NewBadge } from '../../../../components/new-badge';
import { BLUEPRINT_NEW_DAYS } from '../../../../constants/blueprint-new-days';
import { routes } from '../../../../constants/routes';
import { useBlueprintIDSearchParam } from '../../../../hooks/use-blueprint-id-search-param';
import { useSecurityJourneyTranslations } from '../../../../locales/use-security-journey-translations';
import { isNew } from '../../../../utils/is-new';

import styles from './spidr-library-table.module.css';

/* -------------------------------------------------------------------------------------------------
 * Constants
 * -----------------------------------------------------------------------------------------------*/
const HIDDEN_COLUMN_FIELDS_FOR_LEGACY_TIER = new Set(['tier']);

/* -------------------------------------------------------------------------------------------------
 * Utilities
 * -----------------------------------------------------------------------------------------------*/
/**
 * Parses blueprints into the type that is required
 * for the spidr library table
 *
 * @param blueprints all of the blueprints to pass to the table
 */
const parseBlueprintsForTable = (
  blueprints: BlueprintWithOrganizationData[],
) => {
  return blueprints.map(
    ({
      id,
      isFavourited,
      name,
      createdAt,
      tier,
      lastDeliveredAt,
      lastDeliveredSpidrId,
    }) => {
      return {
        createdAt,
        favorited: isFavourited,
        lastDelivered: {
          spidrID: lastDeliveredSpidrId,
          date: lastDeliveredAt,
        },
        name,
        blueprintID: id,
        tier,
      };
    },
  );
};

/**
 * Compares blueprint names for the purposes of sorting all
 * "new" blueprints to the top of the list and then alphabetical
 * after that
 *
 * @param a name with createdAt
 * @param b name with createdAt
 */
const compareName: GridComparatorFn<SpidrLibraryBlueprint['name']> = (
  _,
  __,
  a,
  b,
) => {
  const aValue = {
    name: a.api.getRow(a.id).name,
    createdAt: a.api.getRow(a.id).createdAt,
  };

  const bValue = {
    name: b.api.getRow(b.id).name,
    createdAt: b.api.getRow(b.id).createdAt,
  };

  const aIsNew = isNew(aValue.createdAt, BLUEPRINT_NEW_DAYS);
  const bIsNew = isNew(bValue.createdAt, BLUEPRINT_NEW_DAYS);

  if (aIsNew && !bIsNew) {
    return -1;
  }

  if (bIsNew && !aIsNew) {
    return 1;
  }

  return aValue.name.localeCompare(bValue.name);
};

/**
 * Compares two nullable dates for the purposes of sorting.
 * This function is sorting the dates in ascending order.
 * In the case where one date is null it will be sorted bellow the
 * non-null date.
 *
 * @param a lastDelivered
 * @param b lastDelivered
 */
const compareLastDeliveredDate: GridComparatorFn<
  SpidrLibraryBlueprint['lastDelivered']
> = (a, b) => {
  const aDate = a.date;
  const bDate = b.date;

  if (aDate == null) return 1;
  if (bDate == null) return -1;

  return aDate.getTime() - bDate.getTime();
};

/* -------------------------------------------------------------------------------------------------
 * SpidrLibraryTableToolbar
 * -----------------------------------------------------------------------------------------------*/
const SpidrLibraryTableToolbar = () => {
  /**
   * get an instance of the translations
   */
  const { t } = useSecurityJourneyTranslations();

  return (
    <div className={styles['spidr-library-table-toolbar']}>
      <GridToolbarQuickFilter
        placeholder={t(
          'spidrLibrary.common.table.toolbar.quickFilter.placeholder',
        )}
      />
    </div>
  );
};

/* -------------------------------------------------------------------------------------------------
 * NameCell
 * -----------------------------------------------------------------------------------------------*/
const NameCell = ({
  id,
  value,
  row,
  orgTier,
}: GridRenderCellParams<
  SpidrLibraryBlueprint,
  SpidrLibraryBlueprint['name']
> & {
  orgTier: ConciergeTierInfo;
}) => {
  /**
   * Check if blueprint is a higher tier than the organization and the organization is not legacy
   */
  const blueprintIsHigherTier =
    !orgTier.legacy && Number(row.tier) > Number(orgTier.tier);

  return (
    <div
      className={styles['spidr-library-table-name-cell']}
      data-testid={`name-cell-${id}`}
    >
      <span
        className={styles['spidr-library-table-name-title']}
        data-testid="name"
      >
        {value}
      </span>
      {blueprintIsHigherTier && (
        <span
          className={styles['spidr-library-table-lock']}
          data-testid="name-lock"
        >
          <FontAwesomeIcon icon={faLock} />
        </span>
      )}
      <NewBadge createdAt={row.createdAt} />
    </div>
  );
};

/* -------------------------------------------------------------------------------------------------
 * FavoriteCell
 * -----------------------------------------------------------------------------------------------*/
const FavoriteCell = (
  props: GridRenderCellParams<
    SpidrLibraryBlueprint,
    SpidrLibraryBlueprint['favorited']
  >,
) => {
  return (
    <FavoriteButton
      size="small"
      blueprintID={props.row.blueprintID}
      stopPropagation
    />
  );
};

/* -------------------------------------------------------------------------------------------------
 * TierCell
 * -----------------------------------------------------------------------------------------------*/
const TierCell = (
  props: GridRenderCellParams<
    SpidrLibraryBlueprint,
    SpidrLibraryBlueprint['tier']
  >,
) => {
  return <ConciergeTierBadge.Spidr tier={props.value ?? '1'} />;
};

/* -------------------------------------------------------------------------------------------------
 * LastDeliveredCell
 * -----------------------------------------------------------------------------------------------*/
const LastDeliveredCell = (
  props: GridRenderCellParams<
    SpidrLibraryBlueprint,
    SpidrLibraryBlueprint['lastDelivered']
  >,
) => {
  /**
   * format the date
   */
  const date = useFormatNullableDate(props.value?.date, 'prettyDate', '-');

  /**
   * handle link click to stop event from popagating
   * up to the row and setting the query param
   */
  const handleClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.stopPropagation();
  };

  if (props.value?.date == null || props.value.spidrID == null) {
    return <span data-testid={`last-delivered-${props.id}`}>-</span>;
  }

  return (
    <Link
      data-testid={`last-delivered-${props.id}`}
      to={routes.spidrs.spidrID.index({ spidrID: props.value.spidrID })}
      onClick={handleClick}
    >
      {date}
    </Link>
  );
};

/* -------------------------------------------------------------------------------------------------
 * Columns
 * -----------------------------------------------------------------------------------------------*/
type SpidrLibraryBlueprint = {
  blueprintID: string;
  createdAt: Date;
  favorited: boolean;
  name: string;
  tier: ConciergeTier;
  lastDelivered: {
    spidrID: string | null;
    date?: Date | null;
  };
};

type ColumnDefinition = GridColDef<SpidrLibraryBlueprint>[];

type GetColumnsOptions = {
  orgTier: ConciergeTierInfo;
  t: TFunction<'security-journey', undefined>;
};

const getColumns = ({ orgTier, t }: GetColumnsOptions): ColumnDefinition => {
  const defaultColumns = [
    {
      field: 'favorited',
      headerName: '',
      width: 60,
      align: 'center',
      resizable: false,
      renderCell: FavoriteCell,
    },
    {
      field: 'name',
      headerName: t('spidrLibrary.common.table.header.name'),
      width: 420,
      resizable: true,
      renderCell: (props) => <NameCell {...props} orgTier={orgTier} />,
      sortComparator: compareName,
    },
    {
      field: 'tier',
      headerName: t('spidrLibrary.common.table.header.tier'),
      minWidth: 150,
      resizable: false,
      renderCell: TierCell,
    },
    {
      field: 'lastDelivered',
      headerName: t('spidrLibrary.common.table.header.lastDelivered'),
      minWidth: 150,
      resizable: false,
      renderCell: LastDeliveredCell,
      getSortComparator: (sortDirection) => {
        // Use a custome sort here because we always want a null value to return lower than a
        // non-null value
        // See https://mui.com/x/react-data-grid/sorting/#asymmetric-comparator
        return (value1, value2, cellParams1, cellParams2) => {
          // The sort function will either be a 1 or -1 if one or both of the dates are null
          // otherwise it will be the difference between the two dates in milliseconds
          const sort = compareLastDeliveredDate(
            value1,
            value2,
            cellParams1,
            cellParams2,
          );

          // If one or both of the dates are null, don't apply a modifier.
          if (sort === 1 || sort === -1) {
            return sort;
          }

          // Modifier would typically reverse the direction of sort, so descending is the opposite
          // of ascending
          const modifier = sortDirection === 'desc' ? -1 : 1;

          // Apply the modifier
          return modifier * sort;
        };
      },
    },
  ] satisfies ColumnDefinition;

  if (orgTier.legacy) {
    return defaultColumns.filter(
      (col) => !HIDDEN_COLUMN_FIELDS_FOR_LEGACY_TIER.has(col.field),
    );
  }

  return defaultColumns;
};

/* -------------------------------------------------------------------------------------------------
 * SpidrLibraryTable
 * -----------------------------------------------------------------------------------------------*/
type SpidrLibraryTableProps = {
  /**
   * List of blueprints to render in the table
   */
  blueprints: SpidrLibraryBlueprint[];
  /**
   * Tier info for the organization
   */
  orgTier: ConciergeTierInfo;
};

const SpidrLibraryTable = ({ blueprints, orgTier }: SpidrLibraryTableProps) => {
  /**
   * get an instance of the translations
   */
  const { t } = useSecurityJourneyTranslations();

  /**
   * compute which columns should be rendered
   */
  const columns = useMemo(() => {
    return getColumns({ orgTier, t });
  }, [orgTier, t]);

  /**
   * provides ability to set the blueprintID in the url
   */
  const { setBlueprintID } = useBlueprintIDSearchParam();

  /**
   * handles the row click and sets the `spidrBlueprintID` query param in url
   */
  const handleRowClick: GridEventListener<'rowClick'> = ({ id }) => {
    setBlueprintID(id.toString());
  };

  return (
    <DataGridPro
      rows={blueprints}
      columns={columns}
      getRowId={(row) => row.blueprintID}
      initialState={{
        sorting: {
          sortModel: [{ field: 'name', sort: 'asc' }],
        },
      }}
      slots={{
        toolbar: SpidrLibraryTableToolbar,
      }}
      autosizeOptions={{
        columns: ['name'],
        includeOutliers: true,
        includeHeaders: true,
      }}
      onRowClick={handleRowClick}
      autoPageSize
      hideFooter
      disableColumnMenu
    />
  );
};

export { SpidrLibraryTable, parseBlueprintsForTable };
export type { SpidrLibraryTableProps, SpidrLibraryBlueprint };
